import * as React from 'react';
import { DataLayer } from '@grandvisionhq/analytics';
import { useGoogleMapsContext, MediaQuery, InfoWindow } from '@grandvisionhq/common';
import { LocationSearchMap } from '@grandvisionhq/locations';
import { useIntl } from '@grandvisionhq/state';
import { Components, useStoreFinderContext } from '@grandvisionhq/stores-v2';
import { aabPreferredStoreClickEvent } from '../../../data-layer/actions.js';
import { fromStoreResultToStore } from '../../../pages/BookAppointment/adapters/from-store-result-to-store.js';
import StoreFinderItem from '../StoreFinderItem/StoreFinderItemContainer.js';

const CurrentLocationPin = Components.CurrentLocationPin;
const currentLocationPin = CurrentLocationPin({
    theme: {
        borderColor: '#2B2E31',
        background: '#FFFFFF'
    }
});
const PreferredStorePin = Components.PreferredStorePin;
const preferredStorePinFallback = PreferredStorePin();
const StoreFinderMapContainer = ({ stores , googleMapsId  })=>{
    const { hoveredStore: hoveredStoreInList , isUsingUserLocation , userLocation , isUserLocationRequested , setIsUserLocationRequested , preferredStore: preferredStore1 , activeMapInfoWindow , setActiveMapInfoWindow ,  } = useStoreFinderContext();
    const { googleMapsSettings: { preferredStorePin  } ,  } = useGoogleMapsContext();
    const { getLabel  } = useIntl();
    const [hoveredStore, setHoveredStore] = React.useState(null);
    const handleOnMarkerHover = (code)=>{
        setHoveredStore(code);
    };
    const isPhone = MediaQuery.useIsPhone();
    const [activeStore, setActiveStore] = React.useState(null);
    const handleOnClickUseMyLocation = React.useCallback(()=>{
        if (!isUserLocationRequested) {
            setIsUserLocationRequested(true);
        }
    }, [
        isUserLocationRequested,
        setIsUserLocationRequested
    ]);
    const handleOnMarkerClick = React.useCallback((code)=>{
        if (activeMapInfoWindow === code) {
            setActiveMapInfoWindow(null);
            setActiveStore(null);
            return;
        }
        // We need to expand the list of stores with a preferredStore if present
        const storesWithPreferredStore = preferredStore1 ? [
            ...stores,
            preferredStore1
        ] : stores;
        const store1 = storesWithPreferredStore.find((store)=>store.code === code
        );
        if (store1) {
            setActiveMapInfoWindow(code);
            setActiveStore(fromStoreResultToStore(store1));
        }
    }, [
        activeMapInfoWindow,
        preferredStore1,
        stores,
        setActiveMapInfoWindow
    ]);
    const handleOnMapClick = React.useCallback(()=>{
        setActiveMapInfoWindow(null);
    }, [
        setActiveMapInfoWindow
    ]);
    const handleOnClickPreferredStoreItem = (store)=>{
        DataLayer.pushEvent(aabPreferredStoreClickEvent({
            store,
            itemType: 'map'
        }));
    };
    const preferredStoreMarkerConfig = React.useCallback((preferredStore, excludedFromMapBehavior = true)=>{
        return {
            code: preferredStore.code,
            title: getLabel('appointments.component.storeFinderMap.map.pin.title'),
            position: {
                lat: Number(preferredStore.lat),
                lng: Number(preferredStore.lon)
            },
            pin: preferredStorePin || preferredStorePinFallback,
            infoWindow: activeMapInfoWindow === preferredStore.code && !isPhone && /*#__PURE__*/ React.createElement(InfoWindow, {
                position: {
                    lat: Number(preferredStore.lat),
                    lng: Number(preferredStore.lon)
                },
                onCloseClick: handleOnMapClick
            }, /*#__PURE__*/ React.createElement(StoreFinderItem, {
                variant: "map-item",
                store: fromStoreResultToStore(preferredStore),
                "data-t": "searchResultTile",
                onClick: handleOnClickPreferredStoreItem
            })),
            excludedFromMapBehavior
        };
    }, [
        activeMapInfoWindow,
        getLabel,
        handleOnMapClick,
        isPhone,
        preferredStorePin
    ]);
    const storeMarkers = React.useMemo(()=>{
        return stores.map((store)=>{
            const { code , lat , lon  } = store;
            const storeIsPreferredStore = store.code === (preferredStore1 === null || preferredStore1 === void 0 ? void 0 : preferredStore1.code);
            if (storeIsPreferredStore) {
                return preferredStoreMarkerConfig(store, false);
            }
            return {
                code,
                title: getLabel('appointments.component.storeFinderMap.map.pin.title'),
                optimized: false,
                position: {
                    lat: Number(lat),
                    lng: Number(lon)
                },
                infoWindow: activeMapInfoWindow === code && !isPhone && /*#__PURE__*/ React.createElement(InfoWindow, {
                    position: {
                        lat: Number(lat),
                        lng: Number(lon)
                    },
                    onCloseClick: handleOnMapClick
                }, /*#__PURE__*/ React.createElement(StoreFinderItem, {
                    variant: "map-item",
                    store: fromStoreResultToStore(store),
                    "data-t": "searchResultTile"
                }))
            };
        });
    }, [
        activeMapInfoWindow,
        getLabel,
        handleOnMapClick,
        isPhone,
        preferredStore1 === null || preferredStore1 === void 0 ? void 0 : preferredStore1.code,
        preferredStoreMarkerConfig,
        stores, 
    ]);
    const userLocationMarker = React.useMemo(()=>{
        if (isUsingUserLocation && userLocation) {
            return [
                {
                    position: {
                        lat: userLocation.lat,
                        lng: userLocation.lng
                    },
                    pin: currentLocationPin
                }, 
            ];
        }
        return [];
    }, [
        isUsingUserLocation,
        userLocation
    ]);
    const preferredStoreMarker = React.useMemo(()=>{
        const preferredStoreFoundInList = !!stores.filter((store)=>{
            return store.code === (preferredStore1 === null || preferredStore1 === void 0 ? void 0 : preferredStore1.code);
        }).length;
        // Only add preferred store marker here if not present in search results
        if (preferredStore1 && !preferredStoreFoundInList) {
            return [
                preferredStoreMarkerConfig(preferredStore1)
            ];
        }
        return [];
    }, [
        preferredStore1,
        preferredStoreMarkerConfig,
        stores
    ]);
    const markers = React.useMemo(()=>{
        return [
            ...storeMarkers,
            ...userLocationMarker,
            ...preferredStoreMarker
        ];
    }, [
        preferredStoreMarker,
        storeMarkers,
        userLocationMarker
    ]);
    const center = React.useMemo(()=>{
        if (isUsingUserLocation && userLocation !== undefined) {
            return userLocation;
        }
        return undefined;
    }, [
        userLocation,
        isUsingUserLocation
    ]);
    var ref;
    return /*#__PURE__*/ React.createElement(LocationSearchMap, {
        id: googleMapsId,
        markers: markers,
        markersUpdateConfig: {
            centerToFirst: true,
            zoom: 13
        },
        center: center,
        highlight: (ref = activeStore === null || activeStore === void 0 ? void 0 : activeStore.code) !== null && ref !== void 0 ? ref : null,
        hovered: hoveredStore !== null && hoveredStore !== void 0 ? hoveredStore : hoveredStoreInList === null || hoveredStoreInList === void 0 ? void 0 : hoveredStoreInList.code,
        userLocation: isUsingUserLocation && userLocation ? userLocation : undefined,
        userLocationZoomLevel: 13,
        centerOnUserLocation: true,
        activeLocation: activeStore && isPhone ? /*#__PURE__*/ React.createElement(StoreFinderItem, {
            variant: "map-item",
            store: activeStore
        }) : null,
        onClickUseMyLocation: handleOnClickUseMyLocation,
        onMarkerHover: handleOnMarkerHover,
        onMarkerClick: handleOnMarkerClick,
        onMapClick: handleOnMapClick
    });
};
var StoreFinderMap = StoreFinderMapContainer;

export { StoreFinderMap as default };
//# sourceMappingURL=StoreFinderMapContainer.js.map
